import React from "react";

import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCirclePlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Text } from "@performyard/py-components";

import type { GoalImprovementStatus } from "../types";

/** The card only reads these fields, so goal and form improvements both render through it. */
export type ImprovementCardSuggestion = {
	id: string;
	title: string;
	rationale: string;
	suggestedValue: string;
};

export type ImprovementCardState = {
	suggestion: ImprovementCardSuggestion;
	status: GoalImprovementStatus;
};

type ImprovementCardProps = {
	suggestionState: ImprovementCardState;
	onApply: () => void;
	onDelete: () => void;
};

export const ImprovementCard: React.FC<ImprovementCardProps> = ({ suggestionState, onApply, onDelete }) => {
	const { suggestion, status } = suggestionState;

	if (status === "deleted") {
		return null;
	}

	const isApplied = status === "applied";

	return (
		<div className="flex max-w-full min-w-0 flex-col gap-0">
			{/* Improvement Card */}
			<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-1 p-3">
				<div className="flex items-start gap-1.5">
					<FontAwesomeIcon
						icon={faSparkles}
						className="mt-0.5 shrink-0 text-primary-5"
						style={{ fontSize: "11px" }}
					/>
					<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
						{suggestion.title}
					</Text>
				</div>
				<Text size="caption" color="neutral-7">
					{suggestion.rationale}
				</Text>
				<Text
					size="caption"
					weight="semi-bold"
					color="neutral-9"
					dangerouslySetClassName="whitespace-pre-wrap break-words"
				>
					{`"${suggestion.suggestedValue}"`}
				</Text>
			</div>

			{/* Action Bar */}
			<div className="flex max-w-full min-w-0 items-center justify-between">
				<div className="shrink-0">
					{!isApplied && (
						<ButtonIcon
							icon={faTrashCan}
							onClick={onDelete}
							variant="standard"
							color="neutral"
							shade={5}
							size={16}
							aria-label="Remove improvement suggestion"
						/>
					)}
				</div>
				<div className="shrink-0">
					{isApplied ? (
						<ButtonStandard
							variant="text"
							color="green"
							size="medium"
							iconLeft={faCircleCheck}
							onClick={() => undefined}
							dangerouslySetClassName="pointer-events-none cursor-default !w-auto"
						>
							Change Applied
						</ButtonStandard>
					) : (
						<ButtonStandard
							variant="text"
							color="primary"
							size="medium"
							iconLeft={faCirclePlus}
							onClick={onApply}
							dangerouslySetClassName="!w-auto"
						>
							Apply Change
						</ButtonStandard>
					)}
				</div>
			</div>
		</div>
	);
};

export default ImprovementCard;
